import { useEffect, useRef, useState } from 'react'
import { UUID } from 'crypto'
import {
  Alert,
  Box,
  CircularProgress,
  Switch,
  Typography,
} from '@mui/material'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { AccessibleTooltip } from '@/components/AccessibleTooltip'
import {
  EmailPreferences,
  getEmailPreferences,
  updateEmailPreferences,
  getOrgOppNotificationSetting,
  updateOrgOppNotificationSetting,
} from '@/api/emailPreferencesAPI'

const REQUIRED_TOOLTIP =
  "You'll always receive these — they're essential to your account."

type EditableField = keyof EmailPreferences

export default function NotificationsTab({
  orgUuid,
  orgName,
}: {
  orgUuid: UUID
  orgName: string
}) {
  const { user, token, orgs } = getCurrentUser()
  // The per-org (level 2) controls only make sense for users in >1 org; for a
  // single-org user one toggle is enough and the per-org scaffolding just
  // confuses.
  const multiOrg = (orgs?.length ?? 0) > 1
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saveError, setSaveError] = useState(false)
  // Level-2 (per-org) opportunity-alert opt-out for the selected org.
  const [orgOptIn, setOrgOptIn] = useState<boolean | null>(null)
  const [orgOptInError, setOrgOptInError] = useState(false)
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  useEffect(() => {
    if (!user?.uuid || !token) return
    let cancelled = false
    getEmailPreferences({ user_uuid: user.uuid, token })
      .then((prefs) => {
        if (!cancelled) setPreferences(prefs)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.uuid, token])

  // The per-org setting is loaded separately (and re-loaded on org switch); a
  // failure here shouldn't block the account-wide toggles above.
  useEffect(() => {
    if (!user?.uuid || !token || !orgUuid) return
    let cancelled = false
    getOrgOppNotificationSetting({ user_uuid: user.uuid, org_uuid: orgUuid, token })
      .then((v) => {
        if (!cancelled) setOrgOptIn(v)
      })
      .catch(() => {
        if (!cancelled) setOrgOptInError(true)
      })
    return () => {
      cancelled = true
    }
  }, [user?.uuid, token, orgUuid])

  // Clear any pending debounced saves on unmount.
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      Object.values(timers).forEach(clearTimeout)
    }
  }, [])

  const handleToggleOrg = (value: boolean) => {
    if (!user?.uuid || !token || !orgUuid) return
    const previous = orgOptIn
    setOrgOptIn(value)
    setOrgOptInError(false)
    updateOrgOppNotificationSetting({
      user_uuid: user.uuid as UUID,
      org_uuid: orgUuid,
      token,
      subscribed: value,
    }).catch(() => {
      setOrgOptIn(previous)
      setOrgOptInError(true)
    })
  }

  const handleToggle = (field: EditableField, value: boolean) => {
    if (!preferences || !user?.uuid || !token) return

    const previous = preferences[field]
    // Optimistically reflect the change; debounce the save so rapid toggles
    // collapse into a single request. Revert on failure.
    setPreferences({ ...preferences, [field]: value })
    setSaveError(false)

    const userUuid = user.uuid as UUID
    if (timersRef.current[field]) clearTimeout(timersRef.current[field])
    timersRef.current[field] = setTimeout(() => {
      updateEmailPreferences({
        user_uuid: userUuid,
        token,
        preferences: { [field]: value },
      }).catch(() => {
        setPreferences((prev) => (prev ? { ...prev, [field]: previous } : prev))
        setSaveError(true)
      })
    }, 300)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading notification preferences" />
      </Box>
    )
  }

  if (loadError || !preferences) {
    return (
      <Alert severity="error" sx={{ mt: 2 }} role="alert" aria-live="assertive">
        Could not load your notification preferences. Please refresh and try
        again.
      </Alert>
    )
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 2 }}>
      <Typography variant="body1" sx={{ mb: 3, mt: 2 }}>
        Set your preferences for what we email you about. These settings apply
        to your individual account.
      </Typography>

      {saveError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          role="alert"
          aria-live="assertive"
        >
          Could not save your change. Please try again.
        </Alert>
      )}

      <NotificationRow
        label="Required notifications"
        badge="always on"
        descriptionId="required-notifications-desc"
        description="Essential billing and application messages. You can't unsubscribe from these."
      >
        <AccessibleTooltip title={REQUIRED_TOOLTIP}>
          <Box component="span" sx={{ cursor: 'not-allowed' }}>
            <Switch
              checked
              disabled
              slotProps={{
                input: {
                  'aria-label': 'Required notifications',
                  'aria-describedby': 'required-notifications-desc',
                },
              }}
            />
          </Box>
        </AccessibleTooltip>
      </NotificationRow>

      <NotificationRow
        label="Helpful tips"
        descriptionId="helpful-tips-desc"
        description="How to get the most out of Fundsorter (4 emails over your first 6 weeks, then monthly)."
      >
        <AccessibleTooltip
          title={
            preferences.helpful_tips_subscribed
              ? 'Turn off helpful tips emails'
              : 'Turn on helpful tips emails'
          }
        >
          <Box component="span" tabIndex={-1} sx={{ display: 'inline-flex' }}>
            <Switch
              checked={preferences.helpful_tips_subscribed}
              onChange={(e) =>
                handleToggle('helpful_tips_subscribed', e.target.checked)
              }
              slotProps={{
                input: {
                  'aria-label': 'Helpful tips',
                  'aria-describedby': 'helpful-tips-desc',
                },
              }}
            />
          </Box>
        </AccessibleTooltip>
      </NotificationRow>

      <NotificationRow
        label="Newsletter"
        descriptionId="newsletter-desc"
        description="Featured grants, learning opportunities, events, sector updates, and company news."
      >
        <AccessibleTooltip
          title={
            preferences.newsletter_subscribed
              ? 'Turn off newsletter emails'
              : 'Turn on newsletter emails'
          }
        >
          <Box component="span" tabIndex={-1} sx={{ display: 'inline-flex' }}>
            <Switch
              checked={preferences.newsletter_subscribed}
              onChange={(e) =>
                handleToggle('newsletter_subscribed', e.target.checked)
              }
              slotProps={{
                input: {
                  'aria-label': 'Newsletter',
                  'aria-describedby': 'newsletter-desc',
                },
              }}
            />
          </Box>
        </AccessibleTooltip>
      </NotificationRow>

      {multiOrg ? (
        <>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Opportunity alerts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A weekly round-up of opportunities you've favourited that are
              opening or closing in the week ahead.
            </Typography>
          </Box>

          <NotificationRow
            indent
            label="All my organisations"
            descriptionId="opp-alerts-all-desc"
            description="The opportunity alerts you receive for every organisation you're part of."
          >
            <AccessibleTooltip
              title={
                preferences.opp_notifications_subscribed
                  ? 'Turn off opportunity alerts for all organisations'
                  : 'Turn on opportunity alerts for all organisations'
              }
            >
              <Box component="span" tabIndex={-1} sx={{ display: 'inline-flex' }}>
                <Switch
                  checked={preferences.opp_notifications_subscribed}
                  onChange={(e) =>
                    handleToggle(
                      'opp_notifications_subscribed',
                      e.target.checked,
                    )
                  }
                  slotProps={{
                    input: {
                      'aria-label': 'Opportunity alerts for all organisations',
                      'aria-describedby': 'opp-alerts-all-desc',
                    },
                  }}
                />
              </Box>
            </AccessibleTooltip>
          </NotificationRow>

          <NotificationRow
            indent
            label="Current organisation"
            descriptionId="opp-alerts-current-desc"
            description={`The opportunity alerts you receive for ${orgName}.`}
          >
            <AccessibleTooltip
              title={
                !preferences.opp_notifications_subscribed
                  ? 'Turn on all-organisations alerts first'
                  : (orgOptIn ?? true)
                    ? `Turn off ${orgName}'s opportunity alerts`
                    : `Turn on ${orgName}'s opportunity alerts`
              }
            >
              <Box component="span" tabIndex={-1} sx={{ display: 'inline-flex' }}>
                <Switch
                  checked={
                    (orgOptIn ?? true) && preferences.opp_notifications_subscribed
                  }
                  disabled={
                    !preferences.opp_notifications_subscribed ||
                    orgOptIn === null
                  }
                  onChange={(e) => handleToggleOrg(e.target.checked)}
                  slotProps={{
                    input: {
                      'aria-label': `Opportunity alerts for ${orgName}`,
                      'aria-describedby': 'opp-alerts-current-desc',
                    },
                  }}
                />
              </Box>
            </AccessibleTooltip>
          </NotificationRow>

          {orgOptInError && (
            <Alert
              severity="error"
              sx={{ mt: 1 }}
              role="alert"
              aria-live="assertive"
            >
              Could not update this organisation's opportunity alerts. Please
              try again.
            </Alert>
          )}
        </>
      ) : (
        <NotificationRow
          label="Opportunity alerts"
          descriptionId="opportunity-alerts-desc"
          description="A weekly round-up of opportunities you've favourited that are opening or closing in the week ahead."
        >
          <AccessibleTooltip
            title={
              preferences.opp_notifications_subscribed
                ? 'Turn off opportunity alert emails'
                : 'Turn on opportunity alert emails'
            }
          >
            <Box component="span" tabIndex={-1} sx={{ display: 'inline-flex' }}>
              <Switch
                checked={preferences.opp_notifications_subscribed}
                onChange={(e) =>
                  handleToggle('opp_notifications_subscribed', e.target.checked)
                }
                slotProps={{
                  input: {
                    'aria-label': 'Opportunity alerts',
                    'aria-describedby': 'opportunity-alerts-desc',
                  },
                }}
              />
            </Box>
          </AccessibleTooltip>
        </NotificationRow>
      )}
    </Box>
  )
}

interface NotificationRowProps {
  label: string
  description: string
  descriptionId?: string
  badge?: string
  indent?: boolean
  children: React.ReactNode
}

function NotificationRow({
  label,
  description,
  descriptionId,
  badge,
  indent,
  children,
}: NotificationRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 2,
        pl: indent ? 3 : 0,
        borderBottom: '1px solid #eee',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {badge && (
            <Typography variant="body2" color="text.secondary">
              — {badge}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" id={descriptionId}>
          {description}
        </Typography>
      </Box>
      {children}
    </Box>
  )
}
